import React, { useEffect, useState } from "react";
import {
  Col,
  Container,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Link, useLocation, useParams } from "react-router-dom";
import TechnicalRound from "./TechnicalRound";
import SummaryCard from "components/SummaryCard/SummaryCard";
import FinalRound from "./FinalRound";
import ScreeningRound from "./ScreeningRound";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import { get, uri } from "helpers/api_helper";
import * as api from "../../../constants/api_constants";
import { showErrorToast } from "helpers/toastMessages";

const ViewCandidateProfile = () => {
  const [activeRound, setActiveRound] = useState("screening");
  const [evaluationStatus, setEvaluationStatus] = useState("in progress");
  const [candidateDetails, setCandidateDetails] = useState();
  const [fileModal, setFileModal] = useState({ open: false, url: "" });
  const [apiStatus, setApiStatus] = useState({ loading: false, error: "" });
  const { candidateId } = useParams();
  const location = useLocation();
  const jobId = location.state?.id;

  useEffect(() => {
    fetchCandidatesDetails();
  }, []);

  const fetchCandidatesDetails = async () => {
    try {
      const response = await get(api.VIEW_CANDIDATE_BY_ID(candidateId));
      setCandidateDetails(response?.data);
    } catch (error) {
      console.log("Error fetching Candidate details data");
      showErrorToast("Failed to fetch candidate details");
    }
  };

  const handleViewResume = async () => {
    const resumeId = parseInt(
      candidateDetails?.resumeSupportingDocDto?.uploadResume,
      10
    );

    if (!resumeId) {
      showErrorToast("No resume available for this candidate");
      return;
    }

    setApiStatus({ loading: true, error: "" });
    try {
      const response = await get(
        `${uri}/fileservice/api/files/view/${resumeId}`
      );
      if (response && response.fileContent) {
        const byteCharacters = atob(response.fileContent);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteArray[i] = byteCharacters.charCodeAt(i);
        }

        const fileTypeList = response?.fileName?.split(".");
        const fileType = fileTypeList[fileTypeList.length - 1]?.toLowerCase();
        const mimeTypes = {
          pdf: "application/pdf",
          doc: "application/msword",
          docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        };
        const mimeType = mimeTypes[fileType] || "application/octet-stream";
        const blob = new Blob([byteArray], { type: mimeType });
        const url = URL.createObjectURL(blob);

        if (fileType === "pdf") {
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          setFileModal({ open: true, url });
        }
      } else {
        showErrorToast("Resume file content not found");
      }
    } catch (error) {
      console.error(`Error fetching resume ${resumeId}:`, error);
      showErrorToast(`Failed to fetch resume: ${error.message}`);
    } finally {
      setApiStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleFileModal = () => {
    setFileModal((prev) => ({ ...prev, open: false, url: "" }));
  };

  const rounds = [
    { key: "screening", label: "Screening Round", number: 1 },
    { key: "technical", label: "Technical Round", number: 2 },
    { key: "hr/cultural", label: "Final HR/ Cultural Fit", number: 3 },
  ];

  const getLinkStyles = (key) => ({
    color: activeRound === key ? "#475467" : "#006438",
    textDecoration: "none",
    fontSize: "18px",
    backgroundColor: "white",
  });

  const isRoundCompleted = (roundKey) => {
    const roundIndex = rounds.findIndex((r) => r.key === roundKey);
    const activeIndex = rounds.findIndex((r) => r.key === activeRound);
    return roundIndex < activeIndex && evaluationStatus !== "rejected";
  };

  const getBorderColor = (roundKey, isActive) => {
    if (isActive) {
      if (roundKey === "hr/cultural" && evaluationStatus === "hired") {
        return "#006438";
      }
      return "#025BC3";
    }

    if (isRoundCompleted(roundKey)) {
      return "#006438";
    }

    if (
      roundKey === "screening" &&
      ["rejected", "hold"].includes(evaluationStatus)
    ) {
      return "#006438";
    }

    if (
      roundKey === "technical" &&
      activeRound === "technical" &&
      ["rejected", "hold"].includes(evaluationStatus)
    ) {
      return "#006438";
    }

    if (
      roundKey === "hr/cultural" &&
      activeRound === "hr/cultural" &&
      ["hold", "declined offer", "hired"].includes(evaluationStatus)
    ) {
      return "#006438";
    }

    return "#90A4AE";
  };

  const getRadioColor = (roundKey, isActive) => {
    if (isActive) {
      if (roundKey === "hr/cultural" && evaluationStatus === "hired") {
        return "#006438";
      }
      return "#025BC3";
    }

    if (isRoundCompleted(roundKey)) {
      return "#006438";
    }

    if (
      roundKey === "screening" &&
      ["rejected", "hold"].includes(evaluationStatus)
    ) {
      return "#006438";
    }

    if (
      roundKey === "technical" &&
      activeRound === "technical" &&
      ["rejected", "hold"].includes(evaluationStatus)
    ) {
      return "#006438";
    }

    if (
      roundKey === "hr/cultural" &&
      activeRound === "hr/cultural" &&
      ["hold", "declined offer", "hired"].includes(evaluationStatus)
    ) {
      return "#006438";
    }

    return "#90A4AE";
  };

  const handlePreviousRound = () => {
    const currentIndex = rounds.findIndex((r) => r.key === activeRound);
    if (currentIndex > 0) {
      setActiveRound(rounds[currentIndex - 1].key);
    }
  };

  const handleNextRound = () => {
    const currentIndex = rounds.findIndex((r) => r.key === activeRound);
    if (currentIndex < rounds.length - 1) {
      setActiveRound(rounds[currentIndex + 1].key);
    }
  };

  return (
    <div className="page-content">
      <PageSubHeader
        heading="View Candidate Profile"
        routePath="Home"
        pageName="View Profile"
      />
      <Container fluid className="px-4">
        <Row className="mt-3 d-flex justify-content-between align-items-center">
          <Col className="text-end">
            <Button
              className="px-3 mb-3"
              style={{
                width: "150px",
                height: "60px",
                backgroundColor:
                  evaluationStatus === "rejected"
                    ? "#FFEBEB"
                    : evaluationStatus === "hold"
                    ? "#FFEDE5"
                    : evaluationStatus === "hired"
                    ? "#E7FFED"
                    : evaluationStatus === "declined offer"
                    ? "#FFF1F1"
                    : "#ECF5FF",
                border:
                  evaluationStatus === "rejected"
                    ? "1px solid #F93131"
                    : evaluationStatus === "hold"
                    ? "1px solid #FF7038"
                    : evaluationStatus === "hired"
                    ? "1px solid #006438"
                    : evaluationStatus === "declined offer"
                    ? "1px solid #F70D0D"
                    : "1px solid #025BC3",
                borderRadius: "100px",
                color:
                  evaluationStatus === "rejected"
                    ? "red"
                    : evaluationStatus === "hold"
                    ? "#FF7038"
                    : evaluationStatus === "hired"
                    ? "#006438"
                    : evaluationStatus === "declined offer"
                    ? "#F70D0D"
                    : "#025BC3",
              }}
            >
              {evaluationStatus === "rejected"
                ? "Rejected"
                : evaluationStatus === "hold"
                ? "Hold"
                : evaluationStatus === "hired"
                ? "Hired"
                : evaluationStatus === "declined offer"
                ? "Declined Offer"
                : "In Progress"}
            </Button>
          </Col>
        </Row>

        <SummaryCard title="Candidate Overview">
          <Row className="m-3 mt-0">
            <Col md={6}>
              <p className="sub1">Candidate Name</p>
              <p className="main1">
                {candidateDetails?.generalInfoCandidateDto?.firstName}{" "}
                {candidateDetails?.generalInfoCandidateDto?.lastName}
              </p>
              <p className="sub1">Candidate ID</p>
              <p className="main1">{candidateDetails?.candidateId}</p>
              <p className="sub1">Job Role Applied</p>
              <p className="main1">Frontend Developer</p>
              <p className="sub1">Job ID</p>
              <p className="main1">{jobId || ""}</p>
            </Col>
            <Col md={6}>
              <p className="sub1">Candidate Email</p>
              <p className="main1">
                {candidateDetails?.generalInfoCandidateDto?.email}
              </p>
              <p className="sub1">Candidate Mobile Number</p>
              <p className="main1">
                {candidateDetails?.generalInfoCandidateDto?.mobileNumber}
              </p>
              <p className="sub1">Candidate Profile Submitted On</p>
              <p className="main1">{candidateDetails?.appliedDate}</p>
              <p className="sub1">Resume</p>
              <p
                className="main1"
                style={{ color: "#1E9EFB", cursor: "pointer" }}
                onClick={handleViewResume}
              >
                <i
                  className="fa-solid fa-paperclip"
                  style={{ transform: "rotate(-45deg)", marginRight: "8px" }}
                ></i>
                View Resume
              </p>
            </Col>
          </Row>
        </SummaryCard>

        <Row className="ms-2 mt-3 align-items-center">
          <Col>
            <h4 style={{ color: "#222222" }}>Interview Stages</h4>
          </Col>
          <Col className="text-end mb-3">
            <Button
              onClick={handlePreviousRound}
              disabled={rounds.findIndex((r) => r.key === activeRound) === 0}
              style={{
                backgroundColor: "#025BC3",
                border: "none",
                borderRadius: "8px",
                padding: "8px 16px",
                color: "white",
                marginRight: "10px",
                opacity:
                  rounds.findIndex((r) => r.key === activeRound) === 0
                    ? 0.5
                    : 1,
              }}
            >
              <i className="fa-solid fa-arrow-left"></i> Previous
            </Button>
            <Button
              onClick={handleNextRound}
              disabled={
                rounds.findIndex((r) => r.key === activeRound) ===
                  rounds.length - 1 ||
                evaluationStatus === "rejected" ||
                evaluationStatus === "hold"
              }
              style={{
                backgroundColor: "#025BC3",
                border: "none",
                borderRadius: "8px",
                padding: "8px 16px",
                color: "white",
                opacity:
                  rounds.findIndex((r) => r.key === activeRound) ===
                    rounds.length - 1 ||
                  evaluationStatus === "rejected" ||
                  evaluationStatus === "hold"
                    ? 0.5
                    : 1,
              }}
            >
              Next <i className="fa-solid fa-arrow-right"></i>
            </Button>
          </Col>
        </Row>

        <Row className="ms-2">
          {rounds.map((round) => {
            const isActive = round.key === activeRound;

            return (
              <Col
                key={round.key}
                style={{
                  paddingBottom: "8px",
                  borderRadius: "10px",
                  marginRight: "30px",
                  flex: "30px",
                }}
              >
                <Col>
                  <div
                    style={{
                      backgroundColor: getBorderColor(round.key, isActive),
                      borderRadius: "10px",
                      height: "5px",
                      marginBottom: "10px",
                    }}
                  ></div>
                </Col>
                <Link to="#" style={getLinkStyles(round.key)}>
                  <input
                    type="radio"
                    name="interviewStage"
                    value={round.key}
                    checked={isActive}
                    readOnly
                    style={{
                      width: "20px",
                      height: "20px",
                      marginRight: "8px",
                      accentColor: getRadioColor(round.key, isActive),
                      borderColor: getRadioColor(round.key, isActive),
                      cursor: "default",
                    }}
                  />
                  {round.label}
                </Link>
              </Col>
            );
          })}
        </Row>

        {activeRound === "screening" && (
          <Row>
            <Col>
              <ScreeningRound
                setEvaluationStatus={setEvaluationStatus}
                setActiveRound={setActiveRound}
                evaluationStatus={evaluationStatus}
                jobId={jobId}
                candidatedetails={candidateDetails}
                interviewStageId={
                  candidateDetails?.job?.[0]?.interviews?.[0]?.interviewStageId
                }
              />
            </Col>
          </Row>
        )}

        {activeRound === "technical" && (
          <Row>
            <Col>
              <TechnicalRound
                setEvaluationStatus={setEvaluationStatus}
                setActiveRound={setActiveRound}
                evaluationStatus={evaluationStatus}
                jobId={jobId}
                candidatedetails={candidateDetails}
                interviewStageId={
                  candidateDetails?.job?.[0]?.interviews?.[0]?.interviewStageId
                }
              />
            </Col>
          </Row>
        )}

        {activeRound === "hr/cultural" && (
          <Row>
            <Col>
              <FinalRound
                evaluationStatus={evaluationStatus}
                setEvaluationStatus={setEvaluationStatus}
                setActiveRound={setActiveRound}
                jobId={jobId}
                candidatedetails={candidateDetails}
                interviewStageId={
                  candidateDetails?.job?.[0]?.interviews?.[0]?.interviewStageId
                }
              />
            </Col>
          </Row>
        )}

        <Modal
          isOpen={fileModal.open}
          toggle={handleFileModal}
          centered
          size="lg"
          backdrop="static"
        >
          <ModalHeader toggle={handleFileModal}>View Resume</ModalHeader>
          <ModalBody>
            {fileModal.url ? (
              <iframe
                src={fileModal.url}
                style={{ width: "100%", height: "500px" }}
                title="Resume"
              />
            ) : (
              <div className="text-center">No file available</div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleFileModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default ViewCandidateProfile;
